import { configureAbly, useChannel } from '@ably-labs/react-hooks'
import React, {
    createContext,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useClinic } from '~contexts/clinic-context'
import {
    ActionItemFieldsFragment,
    ActionItemStatus,
    ActionItemType,
    AppointmentStatus,
    FilterOp,
    QueryInput,
    RescheduleStatus,
    useQueryActionItemsQuery,
    useQueryAppointmentActionItemsQuery,
    useQueryRescheduleActionItemsQuery,
    UserNotificationSound,
} from '~graphql/generated/graphql'
import {
    ChannelTypeNames,
    constructChannel,
    getAblyMessageData,
} from '~utils/ably-helpers'
import { useUserContext } from '~config/user-context'
import { AUDIO_SOUNDS, playAudio } from '~utils/audio-helpers'
import {
    DESKTOP_NOTIFICATION_TAGS,
    showDesktopNotification,
} from '~utils/desktop-notifications'

type ActionItemProviderType = {
    actionItems: ActionItemFieldsFragment[]
    isLoading: boolean
    refetch: () => void
    filterTypes: ActionItemType[]
    setFilterTypes: React.Dispatch<React.SetStateAction<ActionItemType[]>>
    assigneeIds: string[]
    setAssigneeIds: React.Dispatch<React.SetStateAction<string[]>>
}

const ActionItemContext = createContext<ActionItemProviderType>({
    actionItems: [],
    isLoading: false,
    refetch: () => {},
    filterTypes: [],
    setFilterTypes: () => {},
    assigneeIds: [],
    setAssigneeIds: () => {},
})

export const ActionItemProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { clinic } = useClinic()
    const { user } = useUserContext()
    const [filterTypes, setFilterTypes] = useState<ActionItemType[]>([])
    const [assigneeIds, setAssigneeIds] = useState<string[]>([])
    const dateNow = useRef(new Date())

    const clinicFilter = {
        column: 'clinic',
        filterOp: FilterOp.Eq,
        value: { string: clinic?.id || '' },
    }

    const {
        data: appointmentData,
        loading: appointmentLoading,
        refetch: refetchAppointments,
    } = useQueryAppointmentActionItemsQuery({
        variables: {
            where: [
                clinicFilter,
                {
                    column: 'status',
                    filterOp: FilterOp.Eq,
                    value: { string: AppointmentStatus.Pending },
                },
                {
                    column: 'clinic_approved_at',
                    filterOp: FilterOp.Is,
                    value: { string: 'null' },
                },
                {
                    column: 'action_items.type',
                    filterOp: FilterOp.Eq,
                    value: { string: ActionItemType.PendingAppointment },
                },
            ],
        },
        skip: !clinic?.id,
    })

    const {
        data: rescheduleData,
        loading: rescheduleLoading,
        refetch: refetchReschedules,
    } = useQueryRescheduleActionItemsQuery({
        variables: {
            where: [
                clinicFilter,
                {
                    column: 'status',
                    filterOp: FilterOp.Eq,
                    value: {
                        string: RescheduleStatus.AwaitingClinicApproval,
                    },
                },
                {
                    column: 'appointment.status', // TODO Find a way to or this with clinic_appt_status
                    filterOp: FilterOp.In,
                    value: {
                        stringArray: [
                            AppointmentStatus.Booked,
                            AppointmentStatus.Confirmed,
                        ],
                    },
                },
                {
                    column: 'requested_start_time',
                    filterOp: FilterOp.Gte,
                    value: { float: dateNow.current.getTime() },
                },
            ],
        },
        skip: !clinic?.id,
    })

    const appointmentActionItems: ActionItemFieldsFragment[] = useMemo(
        () =>
            (appointmentData?.queryAppointments || [])
                .map(a => a.action_items)
                .filter(a => !!a)
                .flat() as ActionItemFieldsFragment[],
        [appointmentData],
    )

    const rescheduleActionItems: ActionItemFieldsFragment[] = useMemo(
        () =>
            (rescheduleData?.queryReschedules || [])
                .map(r => r.action_items)
                .filter(a => !!a)
                .flat() as ActionItemFieldsFragment[],
        [rescheduleData],
    )

    const queryInput: QueryInput[] = useMemo(() => {
        const tempInput: QueryInput[] = [
            clinicFilter,
            {
                column: 'status',
                filterOp: FilterOp.Eq,
                value: { string: ActionItemStatus.Pending },
            },
            {
                column: 'type',
                filterOp: FilterOp.In,
                value: {
                    stringArray: [
                        ActionItemType.General,
                        ActionItemType.OfficeTask,
                        ActionItemType.MissingClient,
                        ActionItemType.MissingPatient,
                        ActionItemType.FormSubmitted,
                        ActionItemType.Prescription,
                        ActionItemType.PetSelfieSubmitted,
                    ],
                },
            },
        ]

        return tempInput
    }, [clinic?.id, filterTypes])

    const {
        data: itemData,
        loading,
        refetch: refetchActionItems,
    } = useQueryActionItemsQuery({
        variables: {
            where: queryInput,
            page: 0,
            pageSize: 100,
            orderBy: {
                column: 'created_at',
                isAscending: false,
            },
        },
        skip: !clinic?.id,
    })

    const refetch = () => {
        if (clinic?.id) {
            refetchAppointments()
            refetchReschedules()
            refetchActionItems()
        }
    }

    const otherActionItems = itemData?.queryActionItems || []

    const actionItems = [
        ...appointmentActionItems,
        ...rescheduleActionItems,
        ...otherActionItems,
    ]

    configureAbly({
        authUrl: `${process.env.GATSBY_REST_API_URL}/ably/auth`,
    })

    useChannel(
        constructChannel(
            ChannelTypeNames.actionItemNotification,
            clinic?.id || '',
        ),
        message => {
            if (
                user?.enabled_notification_sounds?.includes(
                    UserNotificationSound.PendingActions,
                )
            ) {
                playAudio(AUDIO_SOUNDS.actionItem)
                showDesktopNotification('New pending action', {
                    body:
                        getActionItemLabelFromAblyMessage(message) ?? undefined,
                    tag: DESKTOP_NOTIFICATION_TAGS.pendingAction,
                })
            }

            refetch()
        },
    )

    return (
        <ActionItemContext.Provider
            value={{
                actionItems,
                isLoading: loading || appointmentLoading || rescheduleLoading,
                refetch,
                filterTypes,
                setFilterTypes,
                assigneeIds,
                setAssigneeIds,
            }}
        >
            {children}
        </ActionItemContext.Provider>
    )
}

export const useActionItems = () => {
    return useContext(ActionItemContext)
}

const ACTION_ITEM_NOTIFICATION_LABELS: Record<ActionItemType, string> = {
    [ActionItemType.PendingAppointment]:
        'A new appointment request needs approval.',
    [ActionItemType.Reschedule]: 'A new reschedule request needs approval.',
    [ActionItemType.Prescription]: 'A new prescription request needs review.',
    [ActionItemType.Boarding]: 'A new boarding request needs review.',
    [ActionItemType.NewChat]: 'You have a new chat.',
    [ActionItemType.OfficeTask]: 'You have a new office task.',
    [ActionItemType.General]: 'You have a new pending action.',
    [ActionItemType.MissingClient]: 'A client is missing information.',
    [ActionItemType.MissingPatient]: 'A patient is missing information.',
    [ActionItemType.FormSubmitted]: 'A new form was submitted.',
    [ActionItemType.PetSelfieSubmitted]: 'A new pet selfie was submitted.',
}

/**
 * The server publishes the full action item on `actionItemNotification-*`.
 * Maps its `type` to a human sentence for the bubble body; unknown shapes
 * fall back to the generic title-only bubble.
 */
function getActionItemLabelFromAblyMessage(message: unknown): string | null {
    const data = getAblyMessageData(message)
    if (typeof data !== 'object' || data === null || !('type' in data)) {
        return null
    }
    const { type } = data
    if (typeof type !== 'string' || !(type in ActionItemType)) return null
    return ACTION_ITEM_NOTIFICATION_LABELS[type as ActionItemType]
}
